import styled, { css } from 'styled-components';
import media from 'styled-media-query';

type BusinessCtaButtonProps = {
  isFullWidth?: boolean;
};

type DividerProps = {
  orientation: 'vertical' | 'horizontal';
};

export const Container = styled.header`
  height: 8.5rem;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 5rem;
  justify-content: space-between;
  ${({ theme }) => css`
    background: ${theme.colors.white};
    box-shadow: 0px 1px 0px ${theme.colors.border};
  `}

  ${media.lessThan('medium')`
    ${() => css`
      box-shadow: 0px 0px 16px rgba(228, 231, 231, 0.5);

      width: 100%;
      height: 6rem;
      padding: 0 3rem;
      position: fixed;
      /* margin-bottom: 100rem; */
      z-index: 15;
      .logo {
        svg {
          width: 7rem;
        }
      }
    `}
  `}
`;

export const MobileMenu = styled.div`
  position: fixed;
  z-index: 15;
  margin-top: 6rem;
  width: 100%;
  padding: 3rem;
  box-shadow: 0px 0px 16px rgba(228, 231, 231, 0.5);
  ${({ theme }) => css`
    background: ${theme.colors.white};

    button {
      color: ${theme.colors.white};
    }
    ul {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      list-style-type: none;
      margin-bottom: 2rem;

      li {
        &:focus {
          color: ${theme.colors.black};
        }
        outline: none;
        margin-top: 1.5rem;
        font-size: ${theme.font.sizes.medium};
        color: ${theme.colors.contrast};
        font-weight: 400;

        &:hover {
          color: ${theme.colors.black};
          cursor: pointer;
        }

        &.active {
          color: ${theme.colors.black};
        }
      }
    }

    div {
      margin-left: 3.5rem;
      background: ${theme.colors.error};
      color: ${theme.colors.error};
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
    }
  `}
`;

export const LocationBox = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: space-between;

  small {
    ${({ theme }) => css`
      font-size: ${theme.font.sizes.small};
      color: ${theme.colors.primary};
    `}

    &:hover {
      cursor: pointer;
    }
  }

  span {
    ${({ theme }) => css`
      font-size: ${theme.font.sizes.small};
      color: ${theme.colors.highContrast};
    `}
  }
`;

export const Navigation = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;

  div {
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      cursor: pointer;
    }
  }

  ${({ theme }) => css`
    button {
      margin-left: 3.5rem;
      color: ${theme.colors.white};
    }
    ul {
      display: flex;
      flex-direction: row;
      align-items: center;
      list-style-type: none;

      li {
        &:focus {
          color: ${theme.colors.black};
        }
        outline: none;
        margin-left: 3.5rem;
        font-size: ${theme.font.sizes.small};
        color: ${theme.colors.contrast};
        font-weight: 400;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:hover {
          color: ${theme.colors.black};
          cursor: pointer;
        }

        &.active {
          color: ${theme.colors.black};
        }
      }
    }

    .profile-button {
      margin-left: 3.5rem;
      background: ${theme.colors.error};
      color: ${theme.colors.error};
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
    }

    .dropdown-wrapper {
      position: relative;
    }

    .dropdown-menu {
      background: red;
      width: 20rem;
      height: 10rem;
      position: absolute;
      top: 3.5rem;
      right: -2.5rem;
      display: flex;
      flex-direction: column;
      padding: 1.5rem 3rem;
      z-index: 10;

      filter: drop-shadow(0px 4px 16px rgba(20, 20, 20, 0.2));

      ${({ theme }) => css`
        font-size: ${theme.font.sizes.small};
        border-radius: ${theme.border.radius.large};
        background: ${theme.colors.white};

        p {
          margin: 0.75rem 0;
          display: flex;
          color: ${theme.colors.highContrast};
          width: 100%;
          justify-content: flex-start;
          text-align: center;

          &:hover {
            cursor: pointer;
            color: ${theme.colors.black};
          }

          span {
            margin-left: 2rem;
          }
        }
      `}
    }
  `}
`;

export const LeftContent = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 1.5rem;
  }

  ${media.lessThan('medium')`
    ${() => css`
      svg {
        width: 7rem;
      }
    `}
  `}

  span {
    ${({ theme }) => css`
      font-size: ${theme.font.sizes.xsmall};
      color: ${theme.colors.contrast};
    `}
  }

  b {
    ${({ theme }) => css`
      font-size: ${theme.font.sizes.small};
      color: ${theme.colors.primary};
    `}

    b {
      &:hover {
        cursor: pointer;
      }
    }
  }

  div {
    display: flex;
    flex-direction: column;
  }
`;

export const BusinessCtaButton = styled.a<BusinessCtaButtonProps>`
  ${({ theme, isFullWidth = false }) => css`
    width: ${isFullWidth ? '100%' : 'auto'};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;

    border-radius: ${theme.border.radius.large};
    background: ${theme.colors.primary};
    color: ${theme.colors.white};

    font-size: ${theme.font.sizes.xsmall};
    font-weight: 500;
    text-decoration: none;

    padding: 0.875rem 1rem;
    margin-left: ${isFullWidth ? '0' : '3.5rem'};

    transition: 150ms;

    &:hover {
      cursor: pointer;
      background: ${theme.colors.primaryLight};
    }
  `}
`;

export const Divider = styled.hr<DividerProps>`
  ${({ theme, orientation }) => css`
    width: ${orientation === 'vertical' ? '1px' : '100%'};
    height: ${orientation === 'vertical' ? '3rem' : '1px'};
    background: ${theme.colors.contrast};

    margin-left: ${orientation === 'vertical' ? '2rem' : '0'};
    margin-block: ${orientation === 'vertical' ? '0' : '2rem'};
    border: none;
  `}
`;
